/**
 * One hand, up or down: `[seat, value]` sets that seat's vote, `[seat]` flips
 * it. Nothing moves while no nomination is open. The list is REPLACED, never
 * written by index — Vue 2 does not see an index write on an array.
 * Shared by `vote` (incoming frames) and `voteSync` (broadcast).
 */
function castVote(state, [seat, value]) {
  if (!state.nomination) return;
  const votes = Array.from(state.votes);
  votes[seat] = value === undefined ? !votes[seat] : value;
  state.votes = votes;
}

/** FT-1454: which ledger list a badge kind lives in. */
const LEDGER_KEY = { nominator: "nominators", nominee: "nominees" };

/** FT-1454: a ledger value made safe — two de-duplicated lists of seat
 *  indices; anything else reads as empty. */
function cleanNominatedToday(value) {
  const seats = (list) =>
    Array.isArray(list)
      ? [...new Set(list.filter((s) => Number.isInteger(s) && s >= 0))]
      : [];
  return {
    nominators: seats(value && value.nominators),
    nominees: seats(value && value.nominees),
  };
}

/**
 * FT-1454: a vote just COMPLETED — the nominator gets the hand, the nominee
 * the noose. An Exile (a traveller on the block) is not a nomination in
 * BotC's sense and writes neither badge; the storyteller's menu rows can
 * still set one by hand. Called from addHistory only, host only.
 */
function recordNominatedToday(state, players) {
  const [nominatorSeat, nomineeSeat] = state.nomination;
  const nominee = players[nomineeSeat];
  if (nominee && nominee.role && nominee.role.team === "traveler") return;
  const add = (list, seat) =>
    Number.isInteger(seat) && seat >= 0 && !list.includes(seat)
      ? [...list, seat]
      : list;
  state.nominatedToday = {
    nominators: add(state.nominatedToday.nominators, nominatorSeat),
    nominees: add(state.nominatedToday.nominees, nomineeSeat),
  };
}

const state = () => ({
  sessionId: "",
  isSpectator: false,
  isReconnecting: false,
  playerCount: 0,
  ping: 0,
  playerId: "",
  claimedSeat: -1,
  nomination: false,
  votes: [],
  lockedVote: 0,
  votingSpeed: 1500, // FT-1445 (user): the vote timer defaults to 1.5 s per seat (was 3 s)
  isVoteInProgress: false,
  voteHistory: [],
  markedPlayer: -1,
  // FT-1314: THE TIE-CROSS — the auto-mark automation's "tied, nobody hangs
  // as it stands": { seats: [i, …], votes: N } while two (or more) marks
  // stand crossed at the same recorded count, null otherwise. Synced like
  // markedPlayer (its own "markedTie" frame + the full gamestate), rendered
  // by the seats as a struck-through noose on every listed chair. A REAL
  // mark supersedes it (setMarkedPlayer to a seat clears it in the same
  // commit) and the night retires it with the day (socket.js's toggleNight
  // case) — the same lifetime the mark itself has.
  markedTie: null,
  // FT-1454: TODAY'S NOMINATIONS — who has nominated today (the hand badge)
  // and who was nominated today (the noose badge), as seat indices. A
  // nomination COUNTS when its vote completes (addHistory, host only), never
  // when it opens: a nomination cancelled before the sweep leaves no mark.
  // Public, so it rides the full gamestate plus its own "nominatedToday"
  // frame; the host's copy is stashed per town (store/persistence.js).
  // Cleared by the night (the root toggleNight) and a new game (clearEnded);
  // follows seat exchanges like the mark (TownSquare's remapMarked). The
  // storyteller's two seat-menu rows are its manual on/off, both ways.
  nominatedToday: { nominators: [], nominees: [] },
  // FT-1314: THE SIX AUTOMATION FLAGS, MIRRORED. The truth lives on the
  // tower shelf (golem/towerBells — per-town persisted, synced); this copy
  // exists because towerState is not reactive and the night roster getter
  // has to re-run when a toggle flips (the Scarlet Woman row's hide).
  // Written only by socket.js's TOWER_EVENT listener, from
  // golem/automations' automationFlags().
  automations: {
    autoMark: false,
    autoExecute: false,
    autoGhostVote: false,
    autoScarletWoman: false,
    autoStarpass: false,
    autoUndertaker: false,
  },
  // FT-1314: THE STARPASS CHOOSER, both halves of it (the grimoireGrants
  // idiom: host ledger + player flag, neither persisted).
  //   starpassOffer    PLAYER side — the "who inherits" ask standing on this
  //                    client: { minions: [{seat, name}] } or null. Written
  //                    by the socket's "starpass" direct frame; cleared by
  //                    the player's own answer or a null frame.
  //   starpassPending  HOST side — the playerId the chooser was sent to, ""
  //                    when none is outstanding. Only that client's answer
  //                    is honoured (golem/automations.onStarpassPick).
  starpassOffer: null,
  starpassPending: "",
  isVoteHistoryAllowed: true,
  isRolesDistributed: false,
  // FT-880: when the storyteller last called the town back (epoch ms, 0 = not
  // this session). Local to whichever client pressed it — it is not synced and
  // not persisted; it exists so the moment is a fact somewhere rather than only
  // a side effect inside a click handler.
  calledBackAt: 0,
  // FT-1344: when the storyteller last kicked a watcher (epoch ms, 0 = not
  // this session). calledBackAt's own shape and reasoning — the commit is
  // the event; this makes the moment a fact rather than only a side effect.
  lastKickAt: 0,
  // FT-931: THE TOWN HAS ENDED. Written only by the root `endGame` /
  // `clearEnded` mutations (store/index.js) — never here directly — because
  // ending the game also clears the grimoire grants, another module's state
  // that a namespaced mutation in this file cannot reach. See store/index.js's
  // `toggleNight` for the same "root mutation reaches into a submodule" shape
  // already used in this app. (FT-1294: it used to reach for the face-down
  // flag as well; that flag is retired.)
  isEnded: false,
  // 'good' | 'evil' | null — who won, set alongside isEnded and cleared with it.
  winningTeam: null,
  // FT-1003: THE GRANTED GRIMOIRE, both halves of it.
  //
  // `grimoireGrants` is the HOST's ledger — playerId → { pinned } for every
  // seat currently shown the grimoire (the Spy's night row's control writes
  // it). Host-local, not synced, not persisted: a grant is transient session
  // state like `calledBackAt` above, not game state — no prevX snapshot,
  // because "undo" here IS the revoke, and the revoke rebuilds the normal
  // view rather than restoring a stash.
  //
  // `isGrimoireGranted` is the PLAYER's flag — the window is OPEN right now,
  // meaning the host is still pushing changes to this client. Written only by
  // the root grantGrimoire/revokeGrimoire mutations (store/index.js), which
  // also write what the grant delivered onto the roster — the same "root
  // mutation reaches into a submodule" shape isEnded documents above.
  //
  // FT-1295: `hasGrimoireMemory` is the SECOND half, and the two are different
  // questions on purpose:
  //
  //   isGrimoireGranted   is the window open — is the host still updating me
  //   hasGrimoireMemory   have I ever been shown it — do I still HOLD what I saw
  //
  // The close stops updating instead of wiping (user's call: "memory for spy,
  // and it would update again in the next night phase when the storyteller
  // shows it to them"), so everything the grant delivered outlives the window
  // and stays editable on the Spy's own board. The first flag goes false at the
  // close; the second stays true for as long as the DATA does, and is cleared
  // in exactly one place — `clearEnded`, beside the `players/clearRoles` that
  // wipes the table for a new game.
  //
  // The render side reads the second, never the first (golem/bluffs.js's
  // `canSeeBluffs`), because what a client may look at follows from what it is
  // holding, not from whether a socket is still feeding it.
  grimoireGrants: {},
  isGrimoireGranted: false,
  hasGrimoireMemory: false,
  // FT-1200: THE ACCOUNT — who this browser is on the platform, or null.
  // `{ id, name, email }`, written only by golem/account.js (boot /me, the
  // door's sign-in/out). Session state, not game state: not synced, not
  // persisted (the cookie is the persistence), no prevX snapshot.
  account: null,
  // FT-1226: LABS — is the platform's `labs` flag on for THIS caller? Written
  // only by golem/account.js's boot fetch of /api/flags/self. Default false:
  // an unreachable platform, a failed fetch or a switched-off flag all mean
  // the unfinished surfaces (currently the guide) stay hidden. Session state
  // like `account` above: not synced, not persisted, no prevX snapshot.
  labs: false,
  // FT-1200: THE HOST'S SEAT-ACCOUNT LEDGER — playerId → accountId for every
  // claimant who offered one (socket.js's "accountId" direct frame). Host
  // side only; players never hold it. Resolved through each seat's live
  // `player.id` at record time (EndGameOverlay), so a stale entry from a
  // refused or abandoned claim is inert. Transient like grimoireGrants above.
  seatAccounts: {},
  // FT-1344: WHO IS WATCHING — the host's live list of seatless viewers,
  // [{ id, name }] (name is what the watcher's own client offered on its
  // ping; "" when it never said). Host side only, derived by socket.js from
  // its ping roster minus the seated ids — a player's client never receives
  // it. Transient like seatAccounts above: not synced, not persisted.
  spectators: [],
  // FT-1350: THE SPECTATOR COUNT, for everyone — the number the HOST's ping
  // broadcasts in its third slot (socket.js), so a player's pill can say
  // "· N spectators" without ever holding the roster above. On the host's
  // own client the pill counts `spectators` directly and this stays 0.
  spectatorCount: 0
});

// A mutation that stores its payload under one state key, as is.
const set = (key) => (state, value) => {
  state[key] = value;
};

const mutations = {
  setPlayerId(state, playerId) {
    state.playerId = playerId;
  },
  setSpectator(state, isSpectator) {
    state.isSpectator = isSpectator;
  },
  setReconnecting(state, isReconnecting) {
    state.isReconnecting = isReconnecting;
  },
  setPlayerCount(state, count) {
    state.playerCount = count;
  },
  // FT-1350: the broadcast spectator count (see the state note) — validated
  // to a non-negative integer; anything else reads as 0.
  setSpectatorCount(state, n) {
    state.spectatorCount =
      typeof n === "number" && isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  },
  setPing(state, ping) {
    state.ping = ping;
  },
  setVotingSpeed(state, speed) {
    state.votingSpeed = speed;
  },
  setVoteInProgress(state, inProgress) {
    state.isVoteInProgress = inProgress;
  },
  /**
   * FT-1311: THE NOOSE LEAVES A BREADCRUMB. A marked-for-execution state
   * survived past where it should have died in a real game (2026-08-28, no
   * repro), and this mutation is the ONE gate every mark transition passes
   * — host writes, the spectator's "marked" frame, the full-sync copy. So
   * every transition now says who moved it: old seat, new seat, and the
   * top of the call stack (trimmed past this frame — enough to name the
   * committing module). When the noose sticks again, the console holds the
   * whole history of how it got there. `no-console` is off outside
   * production builds (.eslintrc.js); the sweep's own hot path never
   * touches this mutation, so the cost is one line per genuine transition.
   */
  setMarkedPlayer(state, index) {
    if (state.markedPlayer !== index) {
      // Skip this frame and vuex's own commit plumbing (wrappedMutation-
      // Handler / commitIterator / commit) — the first frames left are the
      // component or socket module that asked for the change, which is the
      // fact the breadcrumb exists to hold. (Measured: without the filter
      // the trail was three lines of vuex.esm.js and named nobody.)
      const trail = new Error().stack
        .split("\n")
        .slice(2)
        .filter(
          (line) => !line.includes("vuex") && !line.includes("Array.forEach"),
        )
        .slice(0, 3)
        .map((line) => line.trim())
        .join(" « ");
      // eslint-disable-next-line no-console
      console.warn(
        `[noose] markedPlayer ${state.markedPlayer} → ${index}`,
        trail,
      );
    }
    state.markedPlayer = index;
    // FT-1314: A REAL MARK SUPERSEDES THE TIE-CROSS. The crossed pair means
    // "tied, nobody hangs as it stands", and it stands only until a later
    // higher vote — or the storyteller's own hand — puts somebody on the
    // block for real. Cleared here, in the one gate every mark transition
    // passes, so the host's automation and a spectator's incoming "marked"
    // frame retire the pair identically. A -1 does NOT clear it: lifting a
    // mark is not a verdict, and the tie itself stands with markedPlayer -1.
    if (index >= 0 && state.markedTie) {
      state.markedTie = null;
    }
  },
  /**
   * FT-1314: the tie-cross itself — see the state note. Validated the way
   * every synced field is: a payload that is not {seats:[ints], votes>0}
   * reads as null, so a malformed frame can only ever CLEAR the pair.
   */
  setMarkedTie(state, tie) {
    if (
      tie &&
      Array.isArray(tie.seats) &&
      tie.seats.length >= 2 &&
      tie.seats.every((s) => Number.isInteger(s) && s >= 0) &&
      Number.isFinite(tie.votes) &&
      tie.votes > 0
    ) {
      state.markedTie = {
        seats: [...new Set(tie.seats)],
        votes: Math.round(tie.votes),
      };
    } else {
      state.markedTie = null;
    }
  },
  /** FT-1314: the automation flags' reactive mirror — see the state note. */
  setAutomations(state, flags) {
    const next = {};
    Object.keys(state.automations).forEach((key) => {
      next[key] = !!(flags && flags[key]);
    });
    state.automations = next;
  },
  /** FT-1314: the "who inherits" ask landing on (or leaving) THIS client. */
  setStarpassOffer(state, offer) {
    if (offer && Array.isArray(offer.minions) && offer.minions.length) {
      state.starpassOffer = {
        minions: offer.minions
          .filter((m) => m && Number.isInteger(m.seat))
          .map((m) => ({ seat: m.seat, name: String(m.name || "") })),
      };
      if (!state.starpassOffer.minions.length) state.starpassOffer = null;
    } else {
      state.starpassOffer = null;
    }
  },
  /**
   * FT-1314: the dying Imp answers — the callBack idiom: the commit is the
   * event, socket.js's subscriber is the one place it goes out (direct to
   * the host, stamped with this client's own playerId there). The chooser
   * stands down at the moment of the pick; the truth of what the pick DID
   * comes back on the ordinary frames (the new character, the night row).
   */
  starpassAnswer(state) {
    state.starpassOffer = null;
  },
  /** FT-1314: the host's outstanding chooser — see the state note. */
  setStarpassPending: set("starpassPending"),
  setNomination(state, nomination) {
    state.nomination = nomination;
  },
  setVoteHistoryAllowed(state, allowed) {
    state.isVoteHistoryAllowed = allowed;
  },
  claimSeat(state, seat) {
    state.claimedSeat = seat;
  },
  distributeRoles(state, distributed) {
    state.isRolesDistributed = distributed;
  },
  // FT-1200: the platform account (or null on sign-out) — see the state note.
  setAccount: set("account"),
  // FT-1226: the caller's own `labs` verdict — see the state note.
  setLabs: set("labs"),
  // FT-1200: one claimant's account offer, keyed by the playerId their claim
  // rides under (exact case — it must match the `player.id` the host's
  // roster holds, which is the same value from the same claim frame).
  // A null accountId is a retraction: vacating a seat, or a guest claiming.
  setSeatAccount(state, [playerId, accountId]) {
    if (!playerId || typeof playerId !== "string") return;
    const next = { ...state.seatAccounts };
    if (accountId && typeof accountId === "string") {
      next[playerId] = accountId;
    } else {
      delete next[playerId];
    }
    state.seatAccounts = next;
  },
  /** The town id, sanitized: locale-aware lowercase (the id keys the towns
   *  shelf and per-town stashes, so it must not shift with the browser's
   *  locale handling), then only 0-9 a-z _ -, then the first 24. */
  setSessionId(state, townId) {
    // Golem fork: dashes and underscores are legal town-name characters (our
    // relay lowercases but never strips), and readable minted names like
    // "ravenswood-757" need more room than upstream's 10.
    state.sessionId = townId
      .toLocaleLowerCase()
      .replace(/[^0-9a-z_-]/g, "")
      .substr(0, 24);
  },
  /**
   * Open, sync or close the nomination. Committed bare it clears everything
   * but the pace; socket.js commits it with the full object on every sync
   * and with `{ nomination }` alone on the host's frame.
   */
  nomination(
    state,
    { nomination, votes, votingSpeed, lockedVote, isVoteInProgress } = {},
  ) {
    state.nomination = nomination || false;
    state.votes = votes || [];
    if (votingSpeed) state.votingSpeed = votingSpeed;
    state.lockedVote = lockedVote || 0;
    state.isVoteInProgress = isVoteInProgress || false;
  },
  /**
   * Record the vote that just finished, from the given seats and the open
   * nomination. Nothing is recorded for a player's client when the town has
   * history switched off, nor before the sweep has passed every seat
   * (`lockedVote` beyond the seat count — socket.js reads the newest record
   * on exactly that same test).
   */
  /**
   * FT-1454: today's ledger, whole — the full sync, the "nominatedToday"
   * frame and the host's own reload all land here. Anything that is not two
   * lists of seat indices reads as empty, so a bad frame can only clear.
   */
  setNominatedToday(state, value) {
    state.nominatedToday = cleanNominatedToday(value);
  },
  /**
   * FT-1454: the storyteller's switch on one seat's badge — `kind` is
   * "nominator" (the hand) or "nominee" (the noose); `on` sets it, absent
   * flips it. The ledger is REPLACED, never pushed into (Vue 2).
   */
  toggleNominatedToday(state, { seat, kind, on } = {}) {
    const key = LEDGER_KEY[kind];
    if (!key || !Number.isInteger(seat) || seat < 0) return;
    const list = state.nominatedToday[key];
    const has = list.includes(seat);
    const want = on === undefined ? !has : !!on;
    if (want === has) return;
    state.nominatedToday = {
      ...state.nominatedToday,
      [key]: want ? [...list, seat] : list.filter((s) => s !== seat),
    };
  },
  /** FT-1454: two seats exchanged — both badges follow their players, the
   *  same two-way remap the nomination and the mark get (TownSquare). */
  remapNominatedToday(state, [from, to] = []) {
    const { nominators, nominees } = state.nominatedToday;
    const touched = (s) => s === from || s === to;
    if (!nominators.some(touched) && !nominees.some(touched)) return;
    const swap = (s) => (s === from ? to : s === to ? from : s);
    state.nominatedToday = {
      nominators: nominators.map(swap),
      nominees: nominees.map(swap),
    };
  },
  addHistory(state, players) {
    // FT-1454: the completion test runs first (it used to run second; the
    // two early returns are independent, so the swap changes nothing for
    // the history) because today's ledger is written on completion for
    // every host, whatever the town's history setting.
    if (!state.nomination || !(state.lockedVote > players.length)) return;
    if (!state.isSpectator) recordNominatedToday(state, players);
    if (state.isSpectator && !state.isVoteHistoryAllowed) return;
    const [nominatorSeat, nomineeSeat] = state.nomination;
    const nominee = players[nomineeSeat];
    const isExile = nominee.role.team === "traveler";
    const eligible = isExile
      ? players.length
      : players.filter((seat) => !seat.isDead).length;
    const hands = [];
    players.forEach((seat, index) => {
      if (state.votes[index]) hands.push(seat.name);
    });
    state.voteHistory.push({
      timestamp: new Date(),
      nominator: players[nominatorSeat].name,
      nominee: nominee.name,
      type: isExile ? "Exile" : "Execution",
      majority: Math.ceil(eligible / 2),
      votes: hands,
    });
  },
  clearVoteHistory(state) {
    state.voteHistory = [];
  },
  /**
   * FT-880: call the town back. The socket plugin listens for this and is the
   * one place that decides whether it may go out (storyteller only).
   */
  callBack(state) {
    state.calledBackAt = Date.now();
  },
  /**
   * FT-1344: the host's live watcher list — see the state note. Validated
   * the way every derived list is: anything that is not [{id, name}] rows
   * reads as empty, and a malformed row is dropped rather than rendered.
   */
  setSpectators(state, list) {
    state.spectators = (Array.isArray(list) ? list : [])
      .filter((s) => s && typeof s.id === "string" && s.id)
      .map((s) => ({ id: s.id, name: String(s.name || "") }));
  },
  /**
   * FT-1344: the storyteller shows a watcher out — the callBack idiom: the
   * commit is the event, and the socket plugin (storyteller only there) is
   * the one place it goes out. The list itself is corrected by the host's
   * own bookkeeping the moment the kick lands (setSpectators above), so the
   * only state written here is the moment, calledBackAt's own shape.
   */
  kickSpectator(state) {
    state.lastKickAt = Date.now();
  },
  /**
   * FT-1003: grant, revoke, or re-pin one seat's grimoire window. Host only
   * in practice (the night sheet is the only writer); the socket plugin
   * listens for this mutation and is what actually delivers the change to
   * that one seat. The map is REPLACED rather than mutated so Vue 2 sees the
   * key appear/disappear without a Vue.set import.
   */
  setGrimoireGrant(state, { playerId, granted, pinned }) {
    if (!playerId) return;
    const grants = { ...state.grimoireGrants };
    if (granted) {
      grants[playerId] = { pinned: !!pinned };
    } else {
      delete grants[playerId];
    }
    state.grimoireGrants = grants;
  },
  // two names on purpose: `vote` is silent, `voteSync` is broadcast
  vote: castVote,
  voteSync: castVote,
  /** A count (0 included) sets the sweep's lock; bare advances it one. */
  lockVote(state, lock) {
    state.lockedVote = typeof lock === "number" ? lock : state.lockedVote + 1;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
};
